import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarCheck, Phone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { businessInfo } from "@/data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

export const BookingSuccess = () => {
  const [booking, setBooking] = useState(null);
  const [message, setMessage] = useState("Checking payment status...");
  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setMessage("Your booking request was received. We will call or text if anything else is needed.");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const response = await axios.get(`${API}/bookings/status/${sessionId}`);
        if (response.data.booking) setBooking(response.data.booking);
        if (response.data.payment_status === "paid") {
          setMessage("Payment received. Your appointment is confirmed.");
          return;
        }
        if (response.data.status === "cancelled") {
          setMessage("Checkout was cancelled or expired. Please book again if needed.");
          return;
        }
        if (attempts < 5) setTimeout(poll, 2000);
        else setMessage("Payment is still processing. We will update your booking shortly.");
      } catch (error) {
        setMessage("We could not verify payment yet. Please call or text if you need help.");
      }
    };
    poll();
  }, []);
  const total = booking ? Number(booking.estimated_total || booking.service_price || 0) : 0;
  const discountedTotal = booking ? Number(booking.discounted_estimated_total || booking.estimated_total || booking.service_price || 0) : 0;

  return (
    <main className="grid min-h-screen place-items-center bg-[#050505] px-6 py-16 text-white" data-testid="booking-success-page">
      <section className="w-full max-w-3xl rounded-[2.5rem] border border-white/10 bg-white/[0.04] p-8 shadow-2xl shadow-black/40 backdrop-blur-xl md:p-12" data-testid="booking-success-card">
        <div className="grid h-16 w-16 place-items-center rounded-3xl bg-emerald-400/15 text-emerald-200" data-testid="booking-success-icon"><ShieldCheck className="h-8 w-8" /></div>
        <p className="mt-7 text-sm uppercase tracking-[0.22em] text-emerald-200" data-testid="booking-success-kicker">Thank you for booking with A.J. Auto & RV Care</p>
        <h1 className="mt-3 font-display text-4xl font-light tracking-[-0.04em] sm:text-5xl" data-testid="booking-success-title">Your mobile cleaning is booked</h1>
        <p className="mt-4 text-lg leading-8 text-zinc-300" data-testid="booking-success-message">{message}</p>

        {booking && (
          <div className="mt-8 grid gap-4 rounded-[2rem] border border-white/10 bg-black/25 p-6" data-testid="booking-success-details">
            <p className="font-display text-2xl text-white" data-testid="booking-success-service">{booking.package_label}</p>
              <p className="text-zinc-300" data-testid="booking-success-date"><CalendarCheck className="mr-2 inline h-4 w-4 text-blue-300" /> {booking.appointment_date} · {booking.appointment_window_label}</p>
            <p className="text-sm text-zinc-400" data-testid="booking-success-address">Mobile service address: {booking.service_address}</p>
            {booking.add_on_labels?.length > 0 && <p className="text-sm text-zinc-400" data-testid="booking-success-addons">Add-ons: {booking.add_on_labels.join(", ")}</p>}
            {booking.promo_applied && <p className="text-sm text-emerald-200" data-testid="booking-success-promo">Promo applied: {booking.promo_discount_percent}% off service pricing ({money(booking.promo_discount_amount)} savings)</p>}
              <p className="text-sm text-emerald-200" data-testid="booking-success-status">Status: {booking.status === "confirmed" ? "Booked and confirmed" : booking.status}</p>
            <div className="grid gap-3 sm:grid-cols-2" data-testid="booking-success-estimate-grid">
              <div className="rounded-2xl bg-white/[0.04] p-4" data-testid="booking-success-service-estimate"><span className="block text-xs uppercase tracking-[0.16em] text-zinc-500">Service estimate</span><strong>{money(total)}</strong></div>
              <div className="rounded-2xl bg-white/[0.04] p-4" data-testid="booking-success-final-estimate"><span className="block text-xs uppercase tracking-[0.16em] text-zinc-500">Estimated after deal</span><strong>{money(discountedTotal || total)}</strong></div>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-[2rem] border border-amber-300/20 bg-amber-400/10 p-5 text-sm leading-6 text-amber-100" data-testid="booking-success-policy-note">
          Prices are estimates. Heavily soiled vehicles may incur a $25–$75 upcharge, and mileage may apply outside the Hays, KS area.
        </div>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="booking-success-actions">
          <Button asChild className="rounded-full bg-blue-500 text-white hover:bg-blue-400" data-testid="booking-success-call-button"><a href={`tel:${businessInfo.phone}`} data-testid="booking-success-call-link"><Phone className="h-4 w-4" /> Call / Text</a></Button>
          <Button asChild className="rounded-full border border-white/10 bg-white/10 text-white hover:bg-white/15" data-testid="booking-success-home-button"><a href="/" data-testid="booking-success-home-link">Back to homepage</a></Button>
        </div>
      </section>
    </main>
  );
};